import { doesWorkspaceRoleRequireXgSeat } from "@utils/xg-seat-utils";

/** The steps of the dialog that takes a Sphere XG seat away from a member, in their order */

/**
 * The kind of Sphere XG seat to take away: one assigned to the member as the `activeSeat`
 * permission (the `ACTIVE_SEAT` constraint), or the one an active user holds because of their roles
 * (the `USER_ROLE` constraint), which is freed once they hold no role that needs it
 */

/** A step of the dialog that takes a Sphere XG seat away, with what it is about */

/** The props of a step that lists where the member holds an editor role */

/** What each step is about, as the user reads it above the step */
const STEP_TITLES = {
  teams: "Teams",
  groups: "Groups",
  managedProjects: "Managed projects",
  projects: "Projects",
  workspaceRole: "Workspace role",
  confirm: "Remove seat"
};

/** Where the member holds a role that needs a Sphere XG seat */

/**
 * Returns the steps the user walks through to take a Sphere XG seat away: one for every kind of
 * role the member has to give up first, and last the choice of the new workspace role of an admin,
 * or else the confirmation.
 */
export function getRemoveXgSeatSteps(roles) {
  const keys = [];
  if (roles.hasEditorTeams) {
    keys.push("teams");
  }
  if (roles.hasEditorGroups) {
    keys.push("groups");
  }
  if (roles.hasManagedProjects) {
    keys.push("managedProjects");
  }
  if (roles.hasEditorProjects) {
    keys.push("projects");
  }
  keys.push(roles.isWorkspaceAdmin ? "workspaceRole" : "confirm");
  return keys.map(key => ({
    key,
    title: STEP_TITLES[key]
  }));
}

/**
 * Returns the steps once the user leaves a step, with the steps ahead replaced by the ones found
 * relevant now, since a change in one step may resolve another, e.g. leaving a team that made the
 * member a group manager. The steps the user went through stay, to go back to.
 *
 * @param steps The steps so far
 * @param stepIndex The index of the step the user leaves
 * @param foundSteps The steps found relevant now
 */
export function getStepsAhead(steps, stepIndex, foundSteps) {
  const passedSteps = steps.slice(0, stepIndex + 1);
  const passedKeys = new Set(passedSteps.map(({
    key
  }) => key));
  return [...passedSteps, ...foundSteps.filter(({
    key
  }) => !passedKeys.has(key))];
}

/**
 * Whether the user has to walk through the steps before the seat is taken away, which is not the
 * case when the member holds no role that needs a seat and only the confirmation would be left.
 */
export function hasRolesToResolve(steps) {
  return steps.some(({
    key
  }) => key !== "confirm");
}

/** Whether a step is loading its items, failed to load them, or shows them */

/** Returns how a step shows the fetching status of its items */
export function getListStatus(status) {
  switch (status) {
    case "succeeded":
      return "loaded";
    case "rejected":
      return "failed";
    default:
      return "loading";
  }
}

/** The group roles that let a member make changes, which only a member with a seat may hold */
const EDITOR_GROUP_ROLES = new Set(["COMPANY_MANAGER", "PROJECT_MANAGER"]);

/** The project roles that let a member make changes, which only a member with a seat may hold */
const EDITOR_PROJECT_ROLES = new Set(["RESOURCE_OWNER", "RESOURCE_ADMIN", "RESOURCE_EDITOR"]);

/** The workspace roles that a member without a seat can be given instead of an editor role */
export const WORKSPACE_ROLES_WITHOUT_SEAT = ["COMPANY_VIEWER", "MEMBER"];

/** A workspace role that a member without a seat can hold */

/** Why a role cannot be changed here, when the member only holds it through a team */
export const THROUGH_TEAM_REASON = "This role comes from a team the member belongs to";

/** Why the member cannot be removed from a team that is managed for them */
export const MANAGED_TEAM_REASON = "The members of this team are managed automatically";

/** Why the role of the project manager cannot be changed here */
export const PROJECT_MANAGER_REASON = "To change the project manager, use Change management on the project overview";

/** Returns the teams of the member whose workspace role needs a Sphere XG seat */
export function getEditorTeams(teams) {
  return teams.filter(team => doesWorkspaceRoleRequireXgSeat(team.role));
}

/**
 * Returns the teams the member has to leave before the seat is taken away. For an assigned seat,
 * those whose workspace role needs a Sphere XG seat. For the seat of an active user, every team the
 * member can leave too, since on such a plan any role a team gives in a group or project may keep
 * the member active, and that role cannot be changed anywhere but by leaving the team.
 *
 * @param teams The teams of the member
 * @param seatKind The kind of seat to take away
 */
export function getTeamsToLeave(teams, seatKind) {
  if (seatKind === "assigned") {
    return getEditorTeams(teams);
  }
  return teams.filter(team => !getTeamRemoveBlockReason(team) || doesWorkspaceRoleRequireXgSeat(team.role));
}

/** Returns why the member cannot be removed from a team, or undefined when they can */
export function getTeamRemoveBlockReason(team) {
  return team.category.isManaged ? MANAGED_TEAM_REASON : undefined;
}

/** Whether a group role lets the member make changes, so that it needs a Sphere XG seat */
export function isEditorGroupRole(role) {
  return !!role && EDITOR_GROUP_ROLES.has(role);
}

/** Returns the groups in which the member is a group manager or a project manager */
export function getEditorGroups(groups) {
  return groups.filter(group => isEditorGroupRole(group.role));
}

/**
 * Returns why the group role of the member cannot be changed, or undefined when it can. That is the
 * case when they hold it only through a team. Without the explicit roles loaded nothing is known
 * about it, and the backend decides.
 */
export function getGroupChangeBlockReason(group) {
  if (!group.explicitRoles) {
    return undefined;
  }
  return group.explicitRoles.some(isEditorGroupRole) ? undefined : THROUGH_TEAM_REASON;
}

/** Whether a project role lets the member make changes, so that it needs a Sphere XG seat */
export function isEditorProjectRole(role) {
  return !!role && EDITOR_PROJECT_ROLES.has(role);
}

/** A project of the member, with the group it belongs to */

/** What the projects steps tell when the projects of the member could not be loaded */
export const PROJECTS_FAILED_MESSAGE = "The projects of the member could not be loaded.";

/**
 * Returns the projects in which the member is a manager, an admin or an editor, out of the projects
 * of their groups.
 *
 * @param groups The groups of the member, with the IDs of their projects
 * @param projectsMap The projects of the groups, by ID
 */
export function getMemberEditorProjects(groups, projectsMap) {
  return groups.flatMap(group => (group.projectIds ?? []).flatMap(id => projectsMap[id] ? [{
    project: projectsMap[id],
    group
  }] : [])).filter(({
    project
  }) => isEditorProjectRole(project.role));
}

/**
 * Whether the member is the manager of the project, who cannot be removed from it or be made a
 * viewer, but has to be replaced by another project manager first
 */
export function isProjectManager(project) {
  return project.role === "RESOURCE_OWNER";
}

/**
 * Returns why the project role of the member cannot be changed, or undefined when it can: the
 * project manager can only be replaced on a plan that lets the user choose project managers, else
 * in the project itself, and a role that the member holds only through a team has to be changed
 * in the team.
 *
 * @param project The project, with the role of the member in it
 * @param canAssignManagers Whether the plan lets the user choose the project managers
 */
export function getProjectChangeBlockReason(project, canAssignManagers) {
  if (isProjectManager(project)) {
    return canAssignManagers ? undefined : PROJECT_MANAGER_REASON;
  }
  return isEditorProjectRole(project.explicitRole) ? undefined : THROUGH_TEAM_REASON;
}

/**
 * Whether the member is the only group manager of a group, who the backend does not let leave the
 * group or step down.
 *
 * @param members All the members of the group
 * @param memberIdentity The identity of the member to check
 */
export function isOnlyGroupManager(members, memberIdentity) {
  const managers = members.filter(({
    role
  }) => role === "COMPANY_MANAGER");
  return managers.length === 1 && managers[0].identity === memberIdentity;
}

/**
 * Whether the workspace role assigned to the member is admin, which needs a Sphere XG seat. The
 * workspace roles group manager and project manager follow the group roles, which the previous
 * steps took care of, and an admin role that comes from a team is changed in the team.
 */
export function isWorkspaceAdmin(member) {
  if ("userRole" in member && member.userRole) {
    return member.userRole === "COMPANY_EXECUTIVE";
  }
  return "role" in member && member.role === "COMPANY_EXECUTIVE";
}